import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {getFieldInfoFromDataFlowEdgeHandle} from '../flowchartIdentifierUtils';
import {convertRootRowToMapFromStartRow} from '../mapFromStartUtils';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
  TaskSchemaNodeSectionType,
} from '../processFlowchartConfigEditorConstants';
import {isStaticTaskSchemaNodeData, TaskSchemaStaticNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChangeType, FlowchartNodeChange} from '../processFlowchartConfigEditorUtils';
import getRouterConfigChangesAfterEdgeChange from './getRouterConfigChangesAfterEdgeChange';

/**
 * Adds map-from-start rows directly to the draft node for new data flow edges.
 *
 * This mutates the draft in-place rather than producing a deferred REPLACE change.
 * This is necessary because when multiple map-from-start data flow edges are added
 * in the same batch (e.g., via edge mirroring), each REPLACE would be computed from
 * the original node data. The last REPLACE would overwrite earlier ones, losing all
 * but the last field. By mutating the draft directly, each subsequent call sees the
 * previously added fields and the alreadyHasRow check works correctly.
 */
function applyMapFromStartRowForNewEdge(
  staticFlowchart: StaticTemplateFlowchart,
  newEdge: FlowchartStaticEdgeData,
  externalRootNode?: TaskSchemaStaticNodeData
): void {
  if (newEdge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return;
  }

  const sourceFieldInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle);
  if (sourceFieldInfo.sectionType !== TaskSchemaNodeSectionType.MAP_FROM_START) {
    return;
  }

  const sourceNode = staticFlowchart.nodesStaticData.find((node) => node.id === newEdge.source);
  assert(sourceNode != null, 'Source node should not be null');
  assert(isStaticTaskSchemaNodeData(sourceNode), 'Source node should be a task schema node');
  const mapFromStartIdx = sourceNode.sections.findIndex(
    (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
  );
  assert(mapFromStartIdx >= 0, 'Expected a map from start section to exist', {sourceNode});
  const alreadyHasRow = sourceNode.sections[mapFromStartIdx].sectionRows.some(
    (row) => row.clientId === sourceFieldInfo.fieldClientId
  );
  if (alreadyHasRow) {
    return;
  }

  const rootNode =
    staticFlowchart.nodesStaticData.find(
      (node): node is TaskSchemaStaticNodeData =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) ?? externalRootNode;
  assert(rootNode != null, 'Root node must exist', {staticFlowchart});
  const matchingRow = rootNode.sections
    .find((section) => section.sectionType === TaskSchemaNodeSectionType.INPUTS)
    ?.sectionRows.find((row) => row.clientId === sourceFieldInfo.fieldClientId);
  assert(
    matchingRow != null,
    'Expected to find a row in the root node corresponding to the edge source field',
    {rootNode, sourceNode, newEdge}
  );

  sourceNode.sections[mapFromStartIdx].sectionRows.push(convertRootRowToMapFromStartRow(matchingRow));
}

/**
 * Gets all node changes that must occur as a result of a new edge being added to the flowchart.
 *
 * This function both:
 * - Returns deferred node changes (router config updates)
 * - Directly mutates the draft for map-from-start row additions (to avoid REPLACE conflicts
 *   when multiple edges are added in the same batch)
 *
 * @param staticFlowchart The static flowchart draft the new edge is being added to.
 * @param newEdge The new edge that is being added.
 * @returns Node changes for router configs. Map-from-start changes are applied directly to the draft.
 */
export default function getNodeChangesForNewEdge(
  staticFlowchart: StaticTemplateFlowchart,
  newEdge: FlowchartStaticEdgeData,
  externalRootNode?: TaskSchemaStaticNodeData
): Array<FlowchartNodeChange> {
  applyMapFromStartRowForNewEdge(staticFlowchart, newEdge, externalRootNode);
  return getRouterConfigChangesAfterEdgeChange(staticFlowchart, FlowchartEdgeChangeType.ADD, newEdge.id);
}
